import React, { ReactNode } from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import type {} from '@mui/lab/themeAugmentation';
import ThemeColors from '../../types/ThemeColors';

interface Props {
  themeColors?: ThemeColors;
  children: ReactNode;
}

const defaultColors = {
  themeColors: {
    primary: '#000000',
    secondary: 'rgba(250, 191, 226, 1)',
    error: '#f44336',
    warning: '#ff9800',
    info: '#2196f3',
    success: '#4caf50',
  },
};

const ThemeWrapper = ({ themeColors, children }: Props): JSX.Element => {
  const primary = themeColors?.primary || defaultColors.themeColors.primary;
  const secondary =
    themeColors?.secondary || defaultColors.themeColors.secondary;
  const error = themeColors?.danger || defaultColors.themeColors.error;
  const warning = themeColors?.warning || defaultColors.themeColors.warning;
  const info = themeColors?.tertiary || defaultColors.themeColors.info;
  const success = themeColors?.success || defaultColors.themeColors.success;

  const dsoTheme = themeColors && {
    palette: {
      primary: {
        main: primary,
      },
      secondary: {
        main: secondary,
      },
      error: {
        main: error,
      },
      warning: {
        main: warning,
      },
      info: {
        main: info,
      },
      success: {
        main: success,
      },
    },
    overrides: {
      MuiCssBaseline: {
        a: {
          color: primary,
        },
        'a:hover': {
          color: primary,
        },
      },
      MuiButtonBase: {
        root: {
          '&&:focus': {
            outline: 0,
            boxShadow: `0 0 0 0.2rem ${primary}75`,
          },
        },
      },
      MuiButton: {
        startIcon: {
          '&& span': {
            background: primary,
          },
        },
      },
      MuiToggleButtonGroup: {
        root: {
          display: 'block',
        },
        groupedHorizontal: {
          '&&:not(:first-child)': {
            '&&:not(:disabled)': {
              borderLeft: `1px solid ${primary}`,
            },
          },
        },
      },
      MuiToggleButton: {
        root: {
          border: `1px solid ${primary}`,
          backgroundColor: primary,
          '&&:hover': {
            backgroundColor: primary,
            color: '#ffffff',
          },
          '&$selected': {
            backgroundColor: primary,
            color: '#ffffff',
            '&&:hover': {
              backgroundColor: primary,
              color: '#ffffff',
            },
          },
          '&$disabled': {
            border: 0,
          },
        },
      },
      MuiFormControlLabel: {
        root: {
          marginBottom: '-0.5rem',
          marginTop: '-0.5rem',
        },
      },
    },
  };

  return (
    <ThemeProvider theme={createTheme(dsoTheme)}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
};

ThemeWrapper.defaultProps = defaultColors;

export default ThemeWrapper;
